import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    fetchProfile,
    fetchGameState,
    submitGuess,
    requestHint,
    giveUp,
    fetchPuzzleList,
    login,
    signup,
    logout,
    bridgeUndo,
    bridgeReset,
    fetchAvatars,
    updateAvatar,
    updateEmail,
    updatePassword,
    getFriends,
    getFriendRequests,
    searchFriends,
    sendFriendRequest,
    respondToFriendRequest,
    removeFriend,
    fetchInbox,
    markInboxRead,
    deleteInboxMessages,
    fetchLeaderboard,
    fetchStoreItems,
    purchaseStoreItem,
} from '../services/api';
import { useAppStore } from '../stores/appStore';
import type { User, GameState, SubmitGuessResponse } from '../types';

// ============ User Hooks ============

export function useProfile() {
    const setUser = useAppStore(state => state.setUser);

    return useQuery({
        queryKey: ['profile'],
        queryFn: async () => {
            const profile = await fetchProfile();
            setUser(profile);
            return profile;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 1,
    });
}

export function useLogin() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ username, password }: { username: string; password: string }) =>
            login(username, password),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['profile'] });
        },
    });
}

export function useSignup() {
    const queryClient = useQueryClient();
    const setUser = useAppStore(state => state.setUser);

    return useMutation({
        mutationFn: ({ username, email, password }: {
            username: string;
            email: string;
            password: string
        }) => signup(username, email, password),
        onSuccess: (data) => {
            // Update coins immediately if returned (welcome bonus)
            if (data.coins !== undefined) {
                const currentUser = useAppStore.getState().user;
                if (currentUser) {
                    setUser({ ...currentUser, coins: data.coins });
                }
            }
            queryClient.invalidateQueries({ queryKey: ['profile'] });
        },
    });
}

export function useLogout() {
    const queryClient = useQueryClient();
    const setUser = useAppStore(state => state.setUser);

    return useMutation({
        mutationFn: logout,
        onSuccess: () => {
            setUser(null);
            queryClient.invalidateQueries({ queryKey: ['profile'] });
        },
    });
}

// ============ Game State Hooks ============

export function useGameStateQuery(puzzleId: string | null) {
    const setGameState = useAppStore(state => state.setGameState);
    const setSessionId = useAppStore(state => state.setSessionId);
    const setIsCollab = useAppStore(state => state.setIsCollab);

    const query = useQuery({
        queryKey: ['game', puzzleId],
        queryFn: async () => {
            if (!puzzleId) throw new Error('No puzzle ID');
            const state = await fetchGameState(puzzleId);
            setGameState(state);

            // Extract collab info from state if present (backend includes these)
            const stateWithCollab = state as GameState & { session_id?: string; is_collab?: boolean };
            if (stateWithCollab.session_id) {
                setSessionId(stateWithCollab.session_id);
            }
            // Always update collab state - default to false if not present
            setIsCollab(!!stateWithCollab.is_collab);

            return state;
        },
        enabled: !!puzzleId,
        staleTime: 0, // Always fetch fresh state
        retry: (failureCount, error) => {
            // Don't retry if puzzle is archived or threshold-locked
            if (error instanceof Error && (error.name === 'ArchivedPuzzleError' || error.name === 'ThresholdLockedError')) {
                return false;
            }
            return failureCount < 1;
        },
    });

    // Check if the error is an ArchivedPuzzleError
    const isArchivedError = query.error instanceof Error && query.error.name === 'ArchivedPuzzleError';
    const archivedPuzzleId = isArchivedError ? (query.error as any).puzzleId || puzzleId : null;
    const unlockCost = isArchivedError ? (query.error as any).unlockCost || 100 : 100;

    // Check if the error is a ThresholdLockedError
    const isThresholdLockedError = query.error instanceof Error && query.error.name === 'ThresholdLockedError';
    const thresholdLockedData = isThresholdLockedError ? {
        puzzleId: (query.error as any).puzzleId || puzzleId,
        requiredThreshold: (query.error as any).requiredThreshold || 0,
        currentCompletion: (query.error as any).currentCompletion || 0,
        mode: (query.error as any).mode || 'classic',
    } : null;

    return {
        ...query,
        isArchivedError,
        archivedPuzzleId,
        unlockCost,
        isThresholdLockedError,
        thresholdLockedData,
    };
}

export function useSubmitGuess() {
    const queryClient = useQueryClient();
    const puzzleId = useAppStore(state => state.currentPuzzleId);
    const setGameState = useAppStore(state => state.setGameState);
    const setSessionId = useAppStore(state => state.setSessionId);
    const setIsCollab = useAppStore(state => state.setIsCollab);
    const incrementVersion = useAppStore(state => state.incrementStateVersion);

    return useMutation({
        mutationFn: async (guess: string): Promise<SubmitGuessResponse> => {
            if (!puzzleId) throw new Error('No puzzle ID');
            return submitGuess(puzzleId, guess);
        },
        onMutate: async (guess) => {
            // Cancel outgoing refetches
            await queryClient.cancelQueries({ queryKey: ['game', puzzleId] });

            // Snapshot current state for rollback
            const previousState = queryClient.getQueryData<GameState>(['game', puzzleId]);

            // Optimistic update could go here if needed
            incrementVersion();

            return { previousState, guess };
        },
        onSuccess: (data) => {
            if (data.state) {
                setGameState(data.state);
                queryClient.setQueryData(['game', puzzleId], data.state);

                // Update collab state if present in response
                const stateWithCollab = data.state as GameState & { session_id?: string; is_collab?: boolean };
                if (stateWithCollab.session_id) {
                    setSessionId(stateWithCollab.session_id);
                }
                // Always update collab state - default to false if not present
                setIsCollab(!!stateWithCollab.is_collab);
            }
        },
        onError: (_error, _guess, context) => {
            // Rollback on error
            if (context?.previousState) {
                queryClient.setQueryData(['game', puzzleId], context.previousState);
                setGameState(context.previousState);
            }
        },
    });
}

export function useHintMutation() {
    const queryClient = useQueryClient();
    const puzzleId = useAppStore(state => state.currentPuzzleId);
    const setGameState = useAppStore(state => state.setGameState);

    return useMutation({
        mutationFn: async () => {
            if (!puzzleId) throw new Error('No puzzle ID');
            return requestHint(puzzleId);
        },
        onSuccess: (data) => {
            if (data.state) {
                setGameState(data.state);
                queryClient.setQueryData(['game', puzzleId], data.state);
            }
            // Update user's coin balance immediately
            // Get current user from store to avoid stale closure
            if (data.coins_remaining !== undefined) {
                const currentUser = useAppStore.getState().user;
                if (currentUser) {
                    useAppStore.getState().setUser({ ...currentUser, coins: data.coins_remaining });
                }
            }
        },
    });
}

export function useGiveUp() {
    const queryClient = useQueryClient();
    const puzzleId = useAppStore(state => state.currentPuzzleId);
    const setGameState = useAppStore(state => state.setGameState);

    return useMutation({
        mutationFn: async () => {
            if (!puzzleId) throw new Error('No puzzle ID');
            return giveUp(puzzleId);
        },
        onSuccess: (data) => {
            if (data.state) {
                setGameState(data.state);
                queryClient.setQueryData(['game', puzzleId], data.state);
            }
        },
    });
}

export function useRevealGemLetterMutation() {
    const queryClient = useQueryClient();
    const puzzleId = useAppStore(state => state.currentPuzzleId);
    const setGameState = useAppStore(state => state.setGameState);

    return useMutation({
        mutationFn: async (letter: string) => {
            if (!puzzleId) throw new Error('No puzzle ID');
            const { revealGemLetter } = await import('../services/api');
            return revealGemLetter(puzzleId, letter);
        },
        onSuccess: (data) => {
            if (data.state) {
                setGameState(data.state);
                queryClient.setQueryData(['game', puzzleId], data.state);
            }
        },
    });
}

export function useCompassMutation() {
    const queryClient = useQueryClient();
    const puzzleId = useAppStore(state => state.currentPuzzleId);
    const setGameState = useAppStore(state => state.setGameState);

    return useMutation({
        mutationFn: async () => {
            if (!puzzleId) throw new Error('No puzzle ID');
            const { useCompass } = await import('../services/api');
            return useCompass(puzzleId);
        },
        onSuccess: (data) => {
            if (data.state) {
                setGameState(data.state);
                queryClient.setQueryData(['game', puzzleId], data.state);
            }
            // Update inventory in user store after compass use
            if ((data as any).inventory !== undefined) {
                const currentUser = useAppStore.getState().user;
                if (currentUser) {
                    useAppStore.getState().setUser({ ...currentUser, inventory: (data as any).inventory });
                }
            }
        },
    });
}


// ============ Bridge Hooks ============

export function useBridgeUndo() {
    const queryClient = useQueryClient();
    const puzzleId = useAppStore(state => state.currentPuzzleId);
    const setGameState = useAppStore(state => state.setGameState);

    return useMutation({
        mutationFn: async () => {
            if (!puzzleId) throw new Error('No puzzle ID');
            return bridgeUndo(puzzleId);
        },
        onSuccess: (data) => {
            if (data.state) {
                setGameState(data.state);
                queryClient.setQueryData(['game', puzzleId], data.state);
            }
        },
    });
}

export function useBridgeReset() {
    const queryClient = useQueryClient();
    const puzzleId = useAppStore(state => state.currentPuzzleId);
    const setGameState = useAppStore(state => state.setGameState);

    return useMutation({
        mutationFn: async () => {
            if (!puzzleId) throw new Error('No puzzle ID');
            return bridgeReset(puzzleId);
        },
        onSuccess: (data) => {
            if (data.state) {
                setGameState(data.state);
                queryClient.setQueryData(['game', puzzleId], data.state);
            }
        },
    });
}

export function useBuyBridgeClue() {
    const puzzleId = useAppStore(state => state.currentPuzzleId);

    return useMutation({
        mutationFn: async () => {
            if (!puzzleId) throw new Error('No puzzle ID');
            const { buyBridgeClue } = await import('../services/api');
            return buyBridgeClue(puzzleId);
        },
    });
}

export function useSolveBridgeClue() {
    const queryClient = useQueryClient();
    const puzzleId = useAppStore(state => state.currentPuzzleId);
    const setGameState = useAppStore(state => state.setGameState);

    return useMutation({
        mutationFn: async (answer: string) => {
            if (!puzzleId) throw new Error('No puzzle ID');
            const { solveBridgeClue } = await import('../services/api');
            return solveBridgeClue(puzzleId, answer);
        },
        onSuccess: (data) => {
            if (data.state) {
                setGameState(data.state);
                queryClient.setQueryData(['game', puzzleId], data.state);
            }
        },
    });
}

export function useCancelBridgeClue() {
    const queryClient = useQueryClient();
    const puzzleId = useAppStore(state => state.currentPuzzleId);
    const setGameState = useAppStore(state => state.setGameState);

    return useMutation({
        mutationFn: async () => {
            if (!puzzleId) throw new Error('No puzzle ID');
            const { cancelBridgeClue } = await import('../services/api');
            return cancelBridgeClue(puzzleId);
        },
        onSuccess: (data) => {
            if (data.state) {
                setGameState(data.state);
                queryClient.setQueryData(['game', puzzleId], data.state);
            }
        },
    });
}

// ============ Flip Hooks ============

export function useBuyFlipClue() {
    const queryClient = useQueryClient();
    const puzzleId = useAppStore(state => state.currentPuzzleId);
    const setGameState = useAppStore(state => state.setGameState);

    return useMutation({
        mutationFn: async (targetIndex?: number) => {
            if (!puzzleId) throw new Error('No puzzle ID');
            const { buyFlipClue } = await import('../services/api');
            return buyFlipClue(puzzleId, targetIndex);
        },
        onSuccess: (data) => {
            if (data.state) {
                setGameState(data.state);
                queryClient.setQueryData(['game', puzzleId], data.state);
            }
            // Update user's coin balance immediately
            // Get current user from store to avoid stale closure
            if (data.coins_remaining !== undefined) {
                const currentUser = useAppStore.getState().user;
                if (currentUser) {
                    useAppStore.getState().setUser({ ...currentUser, coins: data.coins_remaining });
                }
            }
        },
    });
}

export function useAnswerThunderword() {
    const queryClient = useQueryClient();
    const puzzleId = useAppStore(state => state.currentPuzzleId);
    const setGameState = useAppStore(state => state.setGameState);

    return useMutation({
        mutationFn: async (answerIndex: number) => {
            if (!puzzleId) throw new Error('No puzzle ID');
            const { answerThunderword } = await import('../services/api');
            return answerThunderword(puzzleId, answerIndex);
        },
        onSuccess: (data) => {
            if (data.state) {
                setGameState(data.state);
                queryClient.setQueryData(['game', puzzleId], data.state);
            }
        },
    });
}

export function useBuyFlipLetter() {
    const queryClient = useQueryClient();
    const puzzleId = useAppStore(state => state.currentPuzzleId);
    const setGameState = useAppStore(state => state.setGameState);

    return useMutation({
        mutationFn: async (letter?: string) => {
            if (!puzzleId) throw new Error('No puzzle ID');
            const { buyFlipLetter } = await import('../services/api');
            return buyFlipLetter(puzzleId, letter);
        },
        onSuccess: (data) => {
            if (data.state) {
                setGameState(data.state);
                queryClient.setQueryData(['game', puzzleId], data.state);
            }
        },
    });
}

export function useBuyEchoHint() {
    const queryClient = useQueryClient();
    const puzzleId = useAppStore(state => state.currentPuzzleId);
    const setGameState = useAppStore(state => state.setGameState);

    return useMutation({
        mutationFn: async (targetIndex: number) => {
            if (!puzzleId) throw new Error('No puzzle ID');
            const { buyEchoHint } = await import('../services/api');
            return buyEchoHint(puzzleId, targetIndex);
        },
        onSuccess: (data) => {
            if (data.state) {
                setGameState(data.state);
                queryClient.setQueryData(['game', puzzleId], data.state);
            }
            // Update inventory in user store
            if ((data as any).inventory !== undefined) {
                const currentUser = useAppStore.getState().user;
                if (currentUser) {
                    useAppStore.getState().setUser({ ...currentUser, inventory: (data as any).inventory });
                }
            }
        },
    });
}

// ============ Store Hooks ============

export function useStoreItems() {
    return useQuery({
        queryKey: ['store', 'items'],
        queryFn: fetchStoreItems,
        staleTime: 60 * 1000, // 1 minute
    });
}

export function usePurchaseItem() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (itemType: string) => purchaseStoreItem(itemType),
        onSuccess: (data) => {
            if (data.coins_remaining !== undefined || data.inventory !== undefined) {
                const currentUser = useAppStore.getState().user;
                if (currentUser) {
                    useAppStore.getState().setUser({
                        ...currentUser,
                        ...(data.coins_remaining !== undefined ? { coins: data.coins_remaining } : {}),
                        ...(data.inventory !== undefined ? { inventory: data.inventory } : {}),
                    });
                }
            }
            // Refresh store items to show updated quantities
            queryClient.invalidateQueries({ queryKey: ['store', 'items'] });
        },
    });
}

// ============ Puzzle List Hooks ============

export function usePuzzleList() {
    const setPuzzles = useAppStore(state => state.setPuzzles);

    return useQuery({
        queryKey: ['puzzles'],
        queryFn: async () => {
            const data = await fetchPuzzleList();
            setPuzzles(data.puzzles);
            return data.puzzles;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}

// ============ Social Hooks ============

export function useAvatars() {
    return useQuery({
        queryKey: ['avatars'],
        queryFn: fetchAvatars,
        staleTime: Infinity,
    });
}

export function useUpdateAvatar() {
    const queryClient = useQueryClient();
    const setUser = useAppStore(state => state.setUser);

    return useMutation({
        mutationFn: updateAvatar,
        onSuccess: (data, variables) => {
            if (data.success) {
                // Update cache manually since backend only returns success/avatar
                queryClient.setQueryData<User | undefined>(['profile'], (oldUser) => {
                    if (!oldUser) return oldUser;
                    const newUser = { ...oldUser, avatar: variables };
                    setUser(newUser); // Also update store
                    return newUser;
                });
            }
        },
    });
}

export function useUpdateEmail() {
    const queryClient = useQueryClient();
    const setUser = useAppStore(state => state.setUser);

    return useMutation({
        mutationFn: updateEmail,
        onSuccess: (data) => {
            if (data.success && data.email) {
                // Update user email in cache
                queryClient.setQueryData<User | undefined>(['profile'], (oldUser) => {
                    if (!oldUser) return oldUser;
                    const newUser = { ...oldUser, email: data.email };
                    setUser(newUser);
                    return newUser;
                });
            }
        },
    });
}

export function useUpdatePassword() {
    return useMutation({
        mutationFn: ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) =>
            updatePassword(currentPassword, newPassword),
    });
}

export function useFriends() {
    return useQuery({
        queryKey: ['friends'],
        queryFn: getFriends,
    });
}

export function useFriendRequests() {
    return useQuery({
        queryKey: ['friendRequests'],
        queryFn: getFriendRequests,
        refetchInterval: 30000, // Poll every 30s
    });
}

export function useFriendSearch(query: string) {
    return useQuery({
        queryKey: ['friendSearch', query],
        queryFn: () => searchFriends(query),
        enabled: query.length >= 2,
    });
}

export function useSendFriendRequest() {
    return useMutation({
        mutationFn: sendFriendRequest,
    });
}

export function useRespondToFriendRequest() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ requestId, accept }: { requestId: number; accept: boolean }) =>
            respondToFriendRequest(requestId, accept),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['friendRequests'] });
            queryClient.invalidateQueries({ queryKey: ['friends'] });
            queryClient.invalidateQueries({ queryKey: ['profile'] }); // Update counts
        },
    });
}

export function useRemoveFriend() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: removeFriend,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['friends'] });
            queryClient.invalidateQueries({ queryKey: ['profile'] });
        },
    });
}

// ============ Inbox Hooks ============

export function useInbox() {
    return useQuery({
        queryKey: ['inbox'],
        queryFn: fetchInbox,
        refetchInterval: 60000,
    });
}

export function useMarkInboxRead() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: markInboxRead,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['inbox'] });
            queryClient.invalidateQueries({ queryKey: ['profile'] }); // Update unread count
        },
    });
}

export function useDeleteInboxMessages() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteInboxMessages,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['inbox'] });
            queryClient.invalidateQueries({ queryKey: ['profile'] });
        },
    });
}

// ============ Leaderboard Hooks ============

export function useLeaderboard() {
    return useQuery({
        queryKey: ['leaderboard'],
        queryFn: fetchLeaderboard,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}
